import './EventFilters.scss'
import { Col, Form, Row, Spinner } from 'react-bootstrap'
import { EventFilterConditionOperatorType, EventFilterConditionType } from '../../../models/EventFilter'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import React, { useEffect, useMemo, useState } from 'react'
import TagsInput from 'react-tagsinput'
import { Button } from '../../../components/Button/Button'
import { EventFilterConditionEdit } from './EventFilters'
import { MultiSelectDropdown, MultiSelectOption } from '../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { useGetIntegrationEntitiesQuery } from '../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useDebouncedCallback } from 'use-debounce'
import { Tooltip } from '../../../components/Tooltip/Tooltip'

const textualConditionOperatorOptions = [
  { id: 'equals', label: 'equals' },
  { id: 'not_equals', label: 'not equals' },
  { id: 'contains', label: 'contains' },
  { id: 'not_contains', label: 'not contains' },
  { id: 'starts_with', label: 'starts with' },
  { id: 'not_starts_with', label: 'not starts with' },
  { id: 'ends_with', label: 'ends with' },
  { id: 'not_ends_with', label: 'not ends with' },
]

const hostConditionOperatorOptions = [{ id: 'me', label: 'is me' }, { id: 'not_me', label: 'is not me' }, ...textualConditionOperatorOptions]

const dropdownConditionOperatorOptions = [
  { id: 'equals', label: 'equals' },
  { id: 'not_equals', label: 'not equals' },
]

const numericConditionOperatorOptions = [
  { id: 'equals', label: 'equals' },
  { id: 'not_equals', label: 'not equals' },
  { id: 'less_than', label: 'less than' },
  { id: 'not_less_than', label: 'not less than' },
  { id: 'greater_than', label: 'greater than' },
  { id: 'not_greater_than', label: 'not greater than' },
]

export type ConditionRowProps = {
  conditionId: string
  condition: EventFilterConditionEdit
  save: (condition: EventFilterConditionEdit) => void
  remove: () => void
}

export const ConditionRow: React.FC<ConditionRowProps> = ({ conditionId, condition, save, remove }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId })
  const hasGoogleIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.GOOGLE_CALENDAR))
  }, [userData])
  const hasOutlookIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.OUTLOOK_CALENDAR))
  }, [userData])
  const hasExchangeIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.EXCHANGE_CALENDAR))
  }, [userData])
  const hasWealthboxIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.WEALTHBOX))
  }, [userData])
  const hasRedtailIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.REDTAIL))
  }, [userData])

  const conditionTypeOptions = [
    { id: 'source', label: 'Source' },
    { id: 'service_id', label: 'Meeting platform' },
    { id: 'host', label: 'Host' },
    { id: 'title', label: 'Title' },
    { id: 'participant_count', label: 'Number of participants' },
  ]
  if (hasWealthboxIntegration) {
    conditionTypeOptions.push({ id: 'wealthbox_event_type', label: 'Wealthbox event type' })
  }
  if (hasRedtailIntegration) {
    conditionTypeOptions.push({ id: 'redtail_type', label: 'Redtail type' }, { id: 'redtail_category', label: 'Redtail category' })
  }

  const saveConditionType = (type: EventFilterConditionType) => {
    const conditionOperatorOptions = getConditionOperatorOptions()
    const operator = conditionOperatorOptions.find((option) => option.id === condition.operator)
    save({ ...condition, ...{ type, value: null }, ...(!operator && { operator: null }) })
  }

  const saveValue = useDebouncedCallback((value) => save({ ...condition, ...{ value } }), 400)

  const SourceValue = () => {
    const sourceOptions = [{ id: 'email', label: 'Email' }]
    if (hasGoogleIntegration) sourceOptions.push({ id: IntegrationServiceId.GOOGLE_CALENDAR, label: 'Google Calendar' })
    if (hasOutlookIntegration) sourceOptions.push({ id: IntegrationServiceId.OUTLOOK_CALENDAR, label: 'Outlook Calendar' })
    if (hasExchangeIntegration) sourceOptions.push({ id: IntegrationServiceId.EXCHANGE_CALENDAR, label: 'Exchange Calendar' })
    if (hasWealthboxIntegration) sourceOptions.push({ id: IntegrationServiceId.WEALTHBOX, label: 'Wealthbox' })
    if (hasRedtailIntegration) sourceOptions.push({ id: IntegrationServiceId.REDTAIL, label: 'Redtail' })

    return (
      <MultiSelectDropdown
        name={`source_${conditionId}`}
        onSelectedChanged={saveValue}
        options={sourceOptions}
        initialSelectedOptions={condition.value ?? []}
        disabled={condition.enforced}
      />
    )
  }

  const ServiceIdValue = () => {
    const serviceOptions = [
      { id: IntegrationServiceId.ZOOM, label: 'Zoom' },
      { id: IntegrationServiceId.MEET, label: 'Google Meet' },
      { id: IntegrationServiceId.TEAMS, label: 'Microsoft Teams' },
      { id: IntegrationServiceId.WEBEX, label: 'Webex' },
      { id: 'null', label: 'In-person & Phone' },
    ]

    return (
      <MultiSelectDropdown
        name={`service_id_${conditionId}`}
        onSelectedChanged={(values) => saveValue(values.map((value) => (value === 'null' ? null : value)))}
        options={serviceOptions}
        initialSelectedOptions={condition?.value?.map((i) => i ?? 'null') ?? []}
        disabled={condition.enforced}
      />
    )
  }

  const WealthboxEventTypeValue = () => {
    const { data: eventCategoriesEntity, isFetching: isEventCategoriesFetching } = useGetIntegrationEntitiesQuery(
      {
        accountId,
        connector: IntegrationServiceId.WEALTHBOX,
        entityType: 'event_categories',
      },
      { skip: !hasWealthboxIntegration },
    )

    const [eventTypes, setEventTypes] = useState<MultiSelectOption[] | null>(null)
    useEffect(() => {
      if (!eventCategoriesEntity) {
        return
      }

      const { eventCategories } = eventCategoriesEntity.data

      if (!eventCategories) {
        return
      }

      const options: MultiSelectOption[] = eventCategories.map((e: { id: number; name: string }) => ({
        id: `${e.id}`,
        label: e.name,
      }))

      setEventTypes(options)
    }, [eventCategoriesEntity])

    return isEventCategoriesFetching ? (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    ) : eventTypes ? (
      <MultiSelectDropdown
        name={`wealthboxEventTypes_${conditionId}`}
        onSelectedChanged={saveValue}
        options={eventTypes}
        initialSelectedOptions={condition.value ?? []}
        disabled={condition.enforced}
      />
    ) : (
      <div>No types found</div>
    )
  }

  const RedtailCategoryValue = () => {
    const [categoryOptions, setCategoryOptions] = useState<MultiSelectOption[] | null>(null)
    const { data: categoriesEntity, isFetching: isCategoriesFetching } = useGetIntegrationEntitiesQuery(
      {
        accountId,
        connector: IntegrationServiceId.REDTAIL,
        entityType: 'categories',
      },
      { skip: !hasRedtailIntegration },
    )
    useEffect(() => {
      if (!categoriesEntity) {
        return
      }
      const { categories } = categoriesEntity.data
      if (!categories) {
        return
      }
      const options = categories.map((e: { id: number; name: string }) => ({
        id: `${e.id}`,
        label: e.name,
      }))
      setCategoryOptions(options)
    }, [categoriesEntity])
    return isCategoriesFetching ? (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    ) : categoryOptions ? (
      <MultiSelectDropdown
        name={`redtailCategories_${conditionId}`}
        onSelectedChanged={saveValue}
        options={categoryOptions}
        initialSelectedOptions={condition.value ?? []}
        disabled={condition.enforced}
      />
    ) : (
      <div>No categories found</div>
    )
  }

  const RedtailTypeValue = () => {
    const [activityCodeOptions, setActivityCodeOptions] = useState<MultiSelectOption[] | null>(null)
    const { data: activityCodesEntity, isFetching: isActivityCodesFetching } = useGetIntegrationEntitiesQuery(
      {
        accountId,
        connector: IntegrationServiceId.REDTAIL,
        entityType: 'activity_codes',
      },
      { skip: !hasRedtailIntegration },
    )

    useEffect(() => {
      if (!activityCodesEntity) {
        return
      }
      const { activityCodes } = activityCodesEntity.data
      if (!activityCodes) {
        return
      }
      const options = activityCodes.map((e: { id: number; name: string }) => ({
        id: `${e.id}`,
        label: e.name,
      }))
      setActivityCodeOptions(options)
    }, [activityCodesEntity])
    return isActivityCodesFetching ? (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    ) : activityCodeOptions ? (
      <MultiSelectDropdown
        name={`redtailType_${conditionId}`}
        onSelectedChanged={saveValue}
        options={activityCodeOptions}
        initialSelectedOptions={condition.value ?? []}
        disabled={condition.enforced}
      />
    ) : (
      <div>No types found</div>
    )
  }

  const NumberValue = () => {
    return (
      <Form.Control
        name={`number_${conditionId}`}
        type='number'
        defaultValue={condition.value && condition.value.length ? condition.value[0] : undefined}
        min={0}
        onBlur={(e) => save({ ...condition, ...{ value: [e.currentTarget.value] } })}
        disabled={condition.enforced}
      />
    )
  }

  const MultiStringValue = () => {
    return (
      <TagsInput
        value={condition.value ?? []}
        onlyUnique={true}
        addOnBlur={true}
        inputProps={{ placeholder: 'New value' }}
        onChange={(value) => save({ ...condition, ...{ value } })}
        disabled={condition.enforced}
      />
    )
  }

  const ValueComponent = () => {
    switch (condition.type) {
      case 'source':
        return <SourceValue />
      case 'service_id':
        return <ServiceIdValue />
      case 'participant_count':
        return <NumberValue />
      case 'wealthbox_event_type':
        return <WealthboxEventTypeValue />
      case 'redtail_category':
        return <RedtailCategoryValue />
      case 'redtail_type':
        return <RedtailTypeValue />
      case 'host':
        return condition.operator && ['me', 'not_me'].includes(condition.operator) ? null : <MultiStringValue />
      default:
        return <MultiStringValue />
    }
  }

  const getConditionOperatorOptions = () => {
    switch (condition.type) {
      case 'host':
        return hostConditionOperatorOptions
      case 'participant_count':
        return numericConditionOperatorOptions
      case 'title':
        return textualConditionOperatorOptions
      default:
        return dropdownConditionOperatorOptions
    }
  }

  const conditionOperatorOptions = getConditionOperatorOptions()

  return (
    <Row className='g-2 align-items-center'>
      <Col xs={12} md>
        <SelectDropdown
          initialSelectedOption={conditionTypeOptions.find((option) => option.id === condition.type) ?? null}
          options={conditionTypeOptions}
          className='py-2 zocks-px-0.75'
          onOptionSelected={(option) => {
            saveConditionType(option.id as EventFilterConditionType)
          }}
          placeholder={`Select filter type`}
          disabled={condition.enforced}
        />
      </Col>
      <Col xs={12} md>
        <SelectDropdown
          initialSelectedOption={conditionOperatorOptions.find((option) => option.id === condition.operator) ?? null}
          options={conditionOperatorOptions}
          className='py-2 zocks-px-0.75'
          onOptionSelected={(option) => save({ ...condition, ...{ operator: option.id as EventFilterConditionOperatorType } })}
          placeholder={`Select operator`}
          disabled={condition.enforced}
        />
      </Col>
      <Col xs={12} md>
        <ValueComponent />
      </Col>
      <Col xs='auto'>
        <div className='d-flex align-items-center gap-2'>
          <Button variant='primaryGhost' className='p-2' onClick={remove} disabled={condition.enforced}>
            <i className='bi bi-trash' />
          </Button>
          {condition.enforced ? (
            <Tooltip className='zocks-text-gray-500' placement='right' title='This condition is enforced by your organization and cannot be changed'>
              <i className='bi bi-info-circle' />
            </Tooltip>
          ) : (
            <span className='invisible'>
              <i className='bi bi-info-circle' />
            </span>
          )}
        </div>
      </Col>
    </Row>
  )
}
